import * as React from 'react';

import { ConsoleDrawer, Loading } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideArrowRight,
  LucideCircle,
  LucideFilePlus2,
  LucidePencil,
  LucideTrash2,
} from 'lucide-react';

import {
  variableHistoryQueryAtom,
  type VariableHistoryEntry,
} from '../../_graphql/queryVariableHistory';
import { useTranslations } from '../../i18n/useTranslations';

interface VariableHistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  variableId: string;
  variableName: string;
}

const $historyVariableId = atom<string | null>(null);

const $historyData = variableHistoryQueryAtom((get) => ({
  variables: { id: get($historyVariableId) ?? '' },
  skip: !get($historyVariableId),
}));

const formatDate = (dateStr: string): string => {
  try {
    const cleaned = dateStr.replace(/\[.*\]$/, '');
    const d = new Date(cleaned);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const valueFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
  useGrouping: false,
});

const formatValue = (val: string | null): string | null => {
  if (val === null) return null;
  const num = Number(val);
  if (!isNaN(num)) return valueFormatter.format(num);
  return val;
};

//
// ⚛️ ⚛️ ⚛️ VARIABLE HISTORY DIALOG ⚛️ ⚛️ ⚛️
//
export const VariableHistoryDialog = ({
  isOpen,
  onClose,
  variableId,
  variableName,
}: VariableHistoryDialogProps) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const { data: entries, isFetching, refetch } = useAtomValue($historyData);
  const setHistoryVariableId = useSetAtom($historyVariableId);

  // Effects
  React.useEffect(() => {
    if (isOpen && variableId) {
      setHistoryVariableId(variableId);
      refetch?.();
    } else if (!isOpen) {
      setHistoryVariableId(null);
    }
  }, [isOpen, variableId, setHistoryVariableId, refetch]);

  return (
    <ConsoleDrawer
      className="@VariableHistoryDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      position="right"
      title={
        <span>
          {t.pricing.variables.history.title}
          <span className="mx-1 text-neutral/30">&middot;</span>
          {variableName}
        </span>
      }
      content={
        <VariableHistoryContent
          isFetching={isFetching}
          entries={entries}
          emptyLabel={t.pricing.variables.history.empty}
        />
      }
    />
  );
};

type VariableHistoryContentProps = {
  isFetching: boolean;
  entries: VariableHistoryEntry[] | undefined;
  emptyLabel: string;
};

//
// ⚛️ ⚛️ ⚛️ VARIABLE HISTORY CONTENT ⚛️ ⚛️ ⚛️
//
const VariableHistoryContent = ({
  isFetching,
  entries,
  emptyLabel,
}: VariableHistoryContentProps) => {
  if (isFetching) {
    return (
      <div className="space-y-4 py-2">
        <Loading />
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-neutral/50">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="@VariableHistoryContent flex flex-col">
      {entries.map((entry, index) => (
        <VariableHistoryEntryRow
          key={entry.id}
          entry={entry}
          isLast={index === entries.length - 1}
        />
      ))}
    </div>
  );
};

type VariableHistoryEntryRowProps = {
  entry: VariableHistoryEntry;
  isLast: boolean;
};

//
// ⚛️ ⚛️ ⚛️ VARIABLE HISTORY ENTRY ROW ⚛️ ⚛️ ⚛️
//
const VariableHistoryEntryRow = ({
  entry,
  isLast,
}: VariableHistoryEntryRowProps) => {
  // Translations
  const t = useTranslations();

  const isCreated = entry.eventType === 'created';
  const isUpdated = entry.eventType === 'updated';
  const isDeleted = entry.eventType === 'deleted';

  const eventLabel = (() => {
    switch (entry.eventType) {
      case 'created':
        return t.pricing.variables.history.events.created;
      case 'deleted':
        return t.pricing.variables.history.events.deleted;
      default:
        return t.pricing.variables.history.events.updated;
    }
  })();

  const formattedPrevious = formatValue(entry.previousValue);
  const formattedUpdated = formatValue(entry.updatedValue);

  return (
    <div className="@VariableHistoryEntryRow grid grid-cols-[auto_1fr] gap-4">
      <div className="grid grid-rows-[auto_1fr] place-items-center">
        <LucideCircle
          size={16}
          className={cn(
            'mb-1 stroke-[3]',
            isCreated && 'text-success',
            isDeleted && 'text-failure',
            !isCreated && !isDeleted && 'text-primary',
          )}
        />
        {!isLast && <div className="h-full border-l-2 border-neutral/10" />}
      </div>
      <div className="pb-6">
        <div className="text-sm text-neutral/40">
          {formatDate(entry.occurredOn)}
          <span className="mx-1">&middot;</span>
          {entry.createdByName}
        </div>
        <div className="mt-1 flex items-center gap-2 text-base font-bold">
          {isCreated && <LucideFilePlus2 size={14} className="text-success" />}
          {isUpdated && <LucidePencil size={14} className="text-primary" />}
          {isDeleted && <LucideTrash2 size={14} className="text-failure" />}
          {eventLabel}
        </div>

        {isUpdated &&
          (formattedPrevious !== null || formattedUpdated !== null) && (
            <div className="mt-2 flex items-center gap-2 rounded-md border border-neutral/10 bg-neutral/3 px-3 py-2 text-sm">
              {formattedPrevious !== null && (
                <span className="text-neutral/50 tabular-nums">
                  {formattedPrevious}
                </span>
              )}
              {formattedPrevious !== null && formattedUpdated !== null && (
                <LucideArrowRight size={14} className="text-neutral/30" />
              )}
              {formattedUpdated !== null && (
                <span className="font-medium tabular-nums">
                  {formattedUpdated}
                </span>
              )}
            </div>
          )}

        {isCreated && formattedUpdated !== null && (
          <div className="mt-2 rounded-md border border-neutral/10 bg-neutral/3 px-3 py-2 text-sm">
            <span className="text-neutral/50">
              {t.pricing.variables.history.initialValue}{' '}
            </span>
            <span className="font-medium tabular-nums">{formattedUpdated}</span>
          </div>
        )}
      </div>
    </div>
  );
};
